'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { EditDiffPreview } from './tool-approval/EditDiffPreview';
import { EmailApprovalPreview } from './tool-approval/EmailApprovalPreview';
import {
  TicketBulkApprovalPreview,
  type TicketBulkPreview,
} from './tool-approval/TicketBulkApprovalPreview';
import {
  isHubOutboundEmailTool,
  resolveInitialConnection,
  resolveInitialSignature,
} from './tool-approval/emailConnection';

/**
 * Tool part with state and approval
 */
interface ToolPartWithState {
  type?: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  approval?: { id?: string; approved?: boolean };
  input?: unknown;
  args?: unknown;
  output?: unknown;
  errorText?: string;
}

export interface ToolApprovalCardProps {
  toolName: string;
  part: ToolPartWithState;
  approvalId: string;
  preview: Record<string, any> | undefined;
  isLoading: boolean;
  /** True when the preview fetch failed (offer Retry instead of a blank card). */
  hasError: boolean;
  onFetchPreview: (toolName: string, toolInput: any, approvalId: string, apiBaseUrl: string) => Promise<void>;
  apiBaseUrl: string;
  onApprove: (approvalId: string) => void;
  onDeny: (approvalId: string) => void;
  /** True while a request is in flight OR streaming — locks the buttons. */
  isBusy: boolean;
  messageIndex: number;
  /** Optional: used for POST/PUT when saving send_email connection preference or override */
  getAuthToken?: () => string | null;
}

export function ToolApprovalCard({
  toolName,
  part,
  approvalId,
  preview,
  isLoading,
  hasError,
  onFetchPreview,
  apiBaseUrl,
  onApprove,
  onDeny,
  isBusy,
  messageIndex,
  getAuthToken,
}: ToolApprovalCardProps) {
  // send_email selection state
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [useAsDefault, setUseAsDefault] = useState(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);
  const [useSignatureAsDefault, setUseSignatureAsDefault] = useState(false);
  // Once the user has responded (Allow/Deny), lock both buttons so an impatient
  // double-click can't fire a second approval (and a second `send_email`
  // override POST). The ref is the synchronous guard (two native clicks can land
  // before the disabled attribute re-renders); state drives the UI.
  const respondedRef = useRef(false);
  const [responded, setResponded] = useState(false);

  const toolInput = part.input || part.args;

  // Fetch preview on first render. Do NOT auto-refetch after an error — Retry is
  // manual, otherwise a failing endpoint would loop.
  useEffect(() => {
    if (!preview && !isLoading && !hasError) {
      onFetchPreview(toolName, toolInput, approvalId, apiBaseUrl);
    }
  }, [toolName, approvalId, preview, isLoading, hasError, onFetchPreview, apiBaseUrl, toolInput]);

  // Initialize selected connection when the email preview loads
  useEffect(() => {
    if (!isHubOutboundEmailTool(toolName)) return;
    const resolved = resolveInitialConnection(preview);
    if (resolved) {
      setSelectedConnectionId(resolved.connectionId);
      setSelectedProvider(resolved.provider);
    }
  }, [toolName, preview]);

  // Initialize selected signature when the preview loads or the connection changes
  useEffect(() => {
    if (!isHubOutboundEmailTool(toolName)) return;
    setSelectedSignatureId(resolveInitialSignature(preview, selectedConnectionId));
  }, [toolName, preview, selectedConnectionId]);

  const handleApprove = useCallback(async () => {
    if (respondedRef.current || isBusy) return;
    respondedRef.current = true;
    setResponded(true);
    if (isHubOutboundEmailTool(toolName) && getAuthToken && selectedConnectionId && selectedProvider) {
      const token = getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const overridePayload = {
        approvalId,
        connectionId: selectedConnectionId,
        provider: selectedProvider,
        // Always send signatureId so "No signature" can clear any AI-provided value
        signatureId: selectedSignatureId ?? null,
      };
      try {
        await fetch(`${apiBaseUrl}/tool-approval-override`, {
          method: 'POST',
          headers,
          body: JSON.stringify(overridePayload),
        });
      } catch (e) {
        console.warn('Failed to save email tool approval override:', e);
      }
      if (useAsDefault) {
        try {
          await fetch(`${apiBaseUrl}/email-send-preference`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ connectionId: selectedConnectionId, provider: selectedProvider }),
          });
        } catch (e) {
          console.warn('Failed to save default email connection:', e);
        }
      }
      if (useSignatureAsDefault && selectedSignatureId) {
        try {
          await fetch(`${apiBaseUrl}/email-signature-preference`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ connectionId: selectedConnectionId, signatureId: selectedSignatureId }),
          });
        } catch (e) {
          console.warn('Failed to save default signature for connection:', e);
        }
      }
    }
    onApprove(approvalId);
  }, [
    isBusy,
    toolName,
    approvalId,
    onApprove,
    apiBaseUrl,
    getAuthToken,
    selectedConnectionId,
    selectedProvider,
    selectedSignatureId,
    useAsDefault,
    useSignatureAsDefault,
  ]);

  const handleDeny = useCallback(() => {
    if (respondedRef.current || isBusy) return;
    respondedRef.current = true;
    setResponded(true);
    onDeny(approvalId);
  }, [isBusy, onDeny, approvalId]);

  const isEditTool = toolName === 'edit_contact' || toolName === 'edit_company';
  // Ticket writes get a resolved list instead of raw UUIDs — a bulk close is
  // only reviewable if the card names the tickets it is about to close.
  const isTicketWriteTool =
    toolName === 'bulk_update_tickets' || toolName === 'update_ticket';
  const ticketPreview =
    isTicketWriteTool && Array.isArray(preview?.tickets)
      ? (preview as TicketBulkPreview)
      : null;
  const disabled = isBusy || responded;

  return (
    <div
      key={`${messageIndex}-${part.toolCallId}`}
      className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
    >
      <p className="font-semibold text-sm text-yellow-900 dark:text-yellow-100 mb-2">
        Approve: {toolName}?
      </p>

      {isEditTool && preview?.current && (
        <EditDiffPreview current={preview.current} proposed={preview.proposed} />
      )}

      {ticketPreview && (
        <TicketBulkApprovalPreview preview={ticketPreview} toolInput={toolInput} />
      )}

      {isHubOutboundEmailTool(toolName) && preview?.input && (
        <EmailApprovalPreview
          toolName={toolName}
          preview={preview}
          selectedConnectionId={selectedConnectionId}
          selectedProvider={selectedProvider}
          selectedSignatureId={selectedSignatureId}
          useAsDefault={useAsDefault}
          useSignatureAsDefault={useSignatureAsDefault}
          onSelectConnection={(connectionId, provider) => {
            setSelectedConnectionId(connectionId);
            setSelectedProvider(provider);
          }}
          onSelectSignature={setSelectedSignatureId}
          onUseAsDefaultChange={setUseAsDefault}
          onUseSignatureAsDefaultChange={setUseSignatureAsDefault}
        />
      )}

      {/* Preview failed — offer Retry rather than leaving the user to approve blind. */}
      {hasError && !preview && (
        <div role="alert" className="mb-3 flex items-center gap-2 text-xs text-red-700 dark:text-red-300">
          <span>Couldn&apos;t load the preview.</span>
          <button
            type="button"
            className="underline font-medium"
            onClick={() => onFetchPreview(toolName, toolInput, approvalId, apiBaseUrl)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Raw input fallback only while we have neither a preview nor an error. */}
      {!preview && !hasError && (
        <pre className="text-xs overflow-auto bg-white dark:bg-slate-900 p-2 rounded mb-3 border border-yellow-100 dark:border-yellow-800">
          {JSON.stringify(toolInput, null, 2)}
        </pre>
      )}

      {isLoading && (
        <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">Loading preview...</p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleDeny}
          disabled={disabled}
          className="min-h-11 min-w-11 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 transition-colors"
        >
          Deny
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={disabled}
          className="min-h-11 min-w-11 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors"
        >
          {responded ? 'Sending…' : 'Allow'}
        </button>
      </div>
    </div>
  );
}
